import { useQueryClient } from "@tanstack/react-query";
import { cx } from "class-variance-authority";
import { useState } from "react";

import { CloseIcon } from "@/assets/icons/general/Close";
import { Button } from "@/components/ui/buttons/Button/Button";
import { IconButton } from "@/components/ui/buttons/IconButton/IconButton";
import { InlineIconButton } from "@/components/ui/buttons/InlineIconButton/InlineIconButton";
import { InputHeader } from "@/components/ui/inputs/shared/InputHeader";
import { TextArea } from "@/components/ui/inputs/TextInput/TextArea";
import { TextInput } from "@/components/ui/inputs/TextInput/TextInput";
import { Modal } from "@/components/ui/overlays/Modal/Modal";
import { Loader } from "@/components/ui/status/Loader/Loader";
import { Typography } from "@/components/ui/text/Typography/Typography";
import { AuthContext } from "@/data/auth/auth.context";
import { EndorsementsQueries } from "@/data/endorsements/endorsements.queries";
import { useEmailValidation } from "@/hooks/useEmailValidation";
import { useToast } from "@/hooks/useToast";

interface IProps {
  isOpen: boolean;
  onClose: () => void;
}

const DEFAULT_MESSAGE = `Hi [name], \nI hope you're doing well! I'm building my profile on Lossdog and would really appreciate if you could endorse me.
You can add your endorsement here: [profile_link]\nI'll be happy to return the favor anytime!\nThanks,\n[your_name]`;

const RequestEndorsementModal = ({ isOpen, onClose }: IProps) => {
  const { validateEmail } = useEmailValidation();
  const { user: authUser } = AuthContext.useAuth();
  const [emails, setEmails] = useState<string[]>([]);
  const [emailInput, setEmailInput] = useState<string>("");
  const [invalidEmails, setInvalidEmails] = useState<string[]>([]);
  const [message, setMessage] = useState<string>(DEFAULT_MESSAGE);
  const queryClient = useQueryClient();
  const { successToast, errorToast } = useToast();
  const { mutate: requestEndorsements, isPending: isRequestingEndorsements } = EndorsementsQueries.useRequest({
    onSuccess: () => {
      successToast({
        text: "Endorsements requested successfully",
      });

      queryClient.invalidateQueries({ queryKey: EndorsementsQueries.keys.getIncoming(50) });
      queryClient.invalidateQueries({ queryKey: EndorsementsQueries.keys.getOutgoing(50) });
      clearStateAndClose();
    },
    onError: () => {
      errorToast({
        text: "Failed to request endorsements",
      });
    },
  });

  const handleAddEmail = (value: string[]) => {
    const invalidEmailsArray = value.filter((email) => !validateEmail(email) || email === authUser?.user?.email);

    if (invalidEmailsArray.length > 0) {
      errorToast({
        text: "There are some invalid emails",
      });
      setInvalidEmails(invalidEmailsArray);
      return;
    }
    setEmails([...emails, ...value]);
  };

  const handleRemoveEmail = (value: string) => {
    setEmails(emails.filter((email) => email !== value));
  };

  const handleEmailInputChange = (value: string) => {
    setInvalidEmails([]);
    if (value.includes(",")) {
      const newEmails = value.split(",").filter((email) => email.trim() !== "");
      handleAddEmail(newEmails);
      setEmailInput("");
      return;
    }
    setEmailInput(value);
  };

  const clearStateAndClose = () => {
    setEmails([]);
    setEmailInput("");
    setMessage(DEFAULT_MESSAGE);
    onClose();
  };

  const sendEndorsementRequests = () => {
    if (emails.length === 0 && emailInput.trim() === "") {
      errorToast({
        text: "Please enter at least one email",
      });
      return;
    }

    if (message.trim() === "") {
      errorToast({
        text: "Please enter a message",
      });
      return;
    }

    if (invalidEmails.length > 0) {
      errorToast({
        text: "Please remove invalid email addresses before submitting the form",
      });
      return;
    }
    if (emails.length === 0 && !validateEmail(emailInput)) {
      errorToast({
        text: "Please enter valid email addresses",
      });
      return;
    }

    if (emailInput === authUser?.user?.email) {
      errorToast({
        text: "You cannot request endorsements from yourself",
      });
      return;
    }

    let emailsToSend = emails;
    if (emailInput.trim() !== "" && !emails.includes(emailInput) && validateEmail(emailInput)) {
      emailsToSend = [...emailsToSend, emailInput];
    }
    requestEndorsements({ data: { emails: emailsToSend, message } });
  };

  return (
    <Modal
      showCoverImage
      isOpen={isOpen}
      onClose={() => {}}
      hideCloseButton
      dialogClassName="p-4 t:!pt-[30px] t:!pb-[36px]"
    >
      <div className="relative flex min-w-[500px] max-w-[500px] flex-1 flex-col overflow-y-auto">
        <div className="relative mb-5 h-[15px]">
          <InlineIconButton
            onPress={onClose}
            icon={CloseIcon}
            className="absolute t:top-0 right-0 z-10 p-0"
            iconClassName="!h-6 !w-6 text-white"
            label="close"
          />
        </div>
        <Typography
          size="h4"
          className="mb-6 text-center font-weight-600 text-primary-200"
        >
          Request Endorsements
        </Typography>
        <Typography
          size="body-paragraph-s"
          className="mb-6 text-white"
        >
          To enhance your profile, please note that endorsement requests will be sent to the email addresses you
          provide. Make sure to separate multiple emails with commas.
        </Typography>

        {/* <TextInput label="Email" variant="filled" value={email} onChange={(e) => setEmail(e.target.value)} /> */}
        <div className="mb-6 flex flex-col gap-1">
          <InputHeader
            label="Emails"
            required
          />
          <div className="flex min-h-8 flex-row flex-wrap items-center gap-1 rounded-2xs bg-neutral-500 p-1">
            {emails.map((email, index) => (
              <div
                key={index}
                className="flex flex-row items-center gap-1 rounded-2xs bg-secondary-200 pl-2"
              >
                <Typography
                  size="body-paragraph-s"
                  variant="default"
                  className="text-white"
                >
                  {email}
                </Typography>
                <IconButton
                  icon={CloseIcon}
                  onPress={() => handleRemoveEmail(email)}
                  label="Remove email"
                  size="small"
                  variant="transparent"
                  className="!h-[30px] !px-1"
                />
              </div>
            ))}
            {invalidEmails.map((email, index) => (
              <div
                key={index}
                className="flex flex-row items-center gap-1 rounded-2xs bg-secondary-200 pl-2"
              >
                <Typography
                  size="body-paragraph-s"
                  variant="default"
                  className="text-danger-200"
                >
                  {email}
                </Typography>
                <IconButton
                  icon={CloseIcon}
                  onPress={() => {
                    setInvalidEmails(invalidEmails.filter((e) => e !== email));
                  }}
                  label="Remove email"
                  size="small"
                  variant="transparent"
                  className="!text-danger-200 !h-[30px] !px-1"
                />
              </div>
            ))}
            <TextInput
              label=""
              variant="filled"
              hideLabel
              className={cx("w-full rounded-2xs", emails.length > 0 ? "max-w-60" : "max-w-full")}
              textfieldClassName="!bg-neutral-500 h-6 !py-1"
              value={emailInput}
              placeholder="Enter one or more email addresses"
              type="email"
              onChange={(e) => {
                handleEmailInputChange(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleAddEmail([e.currentTarget.value]);
                  setEmailInput("");
                }
              }}
            />
          </div>
        </div>
        <TextArea
          className="mb-6"
          label="Message"
          variant="filled"
          value={message}
          onChange={(e) => {
            setMessage(e.target.value);
          }}
          rows={5}
          required
        />
        <div className="flex justify-end gap-5">
          <Button
            variant="outlined"
            onPress={clearStateAndClose}
            isDisabled={isRequestingEndorsements}
            className="!py-2 h-[38px]"
            labelClassName=" !text-labels-m"
          >
            {isRequestingEndorsements ? <Loader /> : "Cancel"}
          </Button>
          <Button
            variant="primary"
            isDisabled={isRequestingEndorsements}
            data-testid="endorsement-save-button"
            className="!py-2 h-[38px]"
            onPress={sendEndorsementRequests}
            labelClassName=" !text-labels-m !font-weight-500"
          >
            {isRequestingEndorsements ? <Loader /> : "Send"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default RequestEndorsementModal;
